import { useState } from 'react';
import { api } from '../api/cliente.js';
import { useSessao } from '../sessao.js';

/**
 * Cadastro de organização.
 *
 * Cria a organização e o primeiro administrador. Ao concluir, já entra no
 * painel — pedir para o usuário fazer login logo depois de digitar a senha é
 * atrito sem ganho.
 */
export function Registro({ aoVoltar }: { aoVoltar: () => void }) {
  const { entrar } = useSessao();
  const [organizacao, setOrganizacao] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const senhaValida = senha.length >= 10 && /[a-zA-Z]/.test(senha) && /\d/.test(senha);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await api.registrar({ organizacao, nome, email, senha });
      // Entra direto: a senha acabou de ser digitada, não faz sentido pedir de novo.
      await entrar(email, senha);
    } catch (erroCadastro) {
      setErro(
        erroCadastro instanceof Error ? erroCadastro.message : 'Não foi possível criar a conta.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-8">
      <form
        onSubmit={enviar}
        className="w-full max-w-[420px] rounded-[var(--radius-card)] border border-borda bg-superficie p-6 shadow-[var(--shadow-card)]"
      >
        <h1 className="text-[length:var(--text-tela)] font-semibold text-texto">Criar conta</h1>
        <p className="mt-1 text-[length:var(--text-secundario)] text-texto-secundario">
          Sua organização começa com as categorias padrão já configuradas.
        </p>

        <Campo
          rotulo="Nome da organização"
          valor={organizacao}
          aoMudar={setOrganizacao}
          placeholder="Padaria do João"
          autoComplete="organization"
        />
        <Campo
          rotulo="Seu nome"
          valor={nome}
          aoMudar={setNome}
          placeholder="João da Silva"
          autoComplete="name"
        />
        <Campo rotulo="E-mail" valor={email} aoMudar={setEmail} tipo="email" autoComplete="email" />
        <Campo
          rotulo="Senha"
          valor={senha}
          aoMudar={setSenha}
          tipo="password"
          autoComplete="new-password"
        />

        <p
          className={`mt-1 text-[length:var(--text-metadado)] ${
            senha.length === 0
              ? 'text-texto-terciario'
              : senhaValida
                ? 'text-entrada-fg'
                : 'text-pendente-fg-sutil'
          }`}
        >
          Mínimo de 10 caracteres, misturando letras e números.
        </p>

        {erro && (
          <p
            role="alert"
            className="mt-3 rounded-[var(--radius-controle)] bg-saida-bg px-3 py-2 text-[length:var(--text-secundario)] text-saida-fg"
          >
            {erro}
          </p>
        )}

        <button
          type="submit"
          disabled={enviando || !senhaValida}
          className="mt-5 h-11 w-full rounded-[var(--radius-controle)] bg-primaria font-medium text-white hover:bg-primaria-hover disabled:opacity-45"
        >
          {enviando ? 'Criando...' : 'Criar conta'}
        </button>

        <button
          type="button"
          onClick={aoVoltar}
          className="mt-3 w-full text-center text-[length:var(--text-secundario)] text-primaria hover:underline"
        >
          Já tenho conta
        </button>
      </form>
    </div>
  );
}

function Campo({
  rotulo,
  valor,
  aoMudar,
  tipo = 'text',
  placeholder,
  autoComplete,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  tipo?: string;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="mt-3 block">
      <span className="text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
        {rotulo}
      </span>
      <input
        type={tipo}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        required
        placeholder={placeholder ?? ''}
        autoComplete={autoComplete ?? ''}
        className="mt-1 h-11 w-full rounded-[var(--radius-controle)] border border-borda-input bg-superficie px-3 text-[length:var(--text-corpo)] text-texto placeholder:text-[var(--cor-placeholder)]"
      />
    </label>
  );
}
